import {ChevronRightIcon} from "lucide-react";

import {Button} from "@/components/ui/button";
import {cn} from "@/lib/utils";

import {
  RESIDENT_HOME_PILLAR_FOOTER_CLASS,
  RESIDENT_HOME_TEXT_INSET_CLASS,
} from "../lib/resident-home-block-layout";

type ResidentHomeCardPreviewFooterProps = {
  showingCount: number;
  totalRecords: number;
  onShowMore?: () => void;
  className?: string;
};

/** Pagination-style footer: count left, ghost icon action right (matches table pagination). */
export function ResidentHomeCardPreviewFooter({
  showingCount,
  totalRecords,
  onShowMore,
  className,
}: ResidentHomeCardPreviewFooterProps) {
  if (totalRecords <= 0) {
    return null;
  }

  const shown = Math.min(showingCount, totalRecords);
  const remaining = totalRecords - shown;
  const hasMore = remaining > 0;
  const showAction = hasMore && Boolean(onShowMore);

  return (
    <div className={cn(RESIDENT_HOME_PILLAR_FOOTER_CLASS, className)}>
      <div
        className={cn(
          "flex h-8 items-center justify-between gap-2",
          RESIDENT_HOME_TEXT_INSET_CLASS,
        )}
      >
        <p className="text-muted-foreground min-w-0 text-xs tabular-nums">
          Showing {shown} of {totalRecords}
          {hasMore && !onShowMore ? (
            <span className="text-muted-foreground/80">
              {" "}
              · {remaining} more
            </span>
          ) : null}
        </p>
        <div className="flex h-8 shrink-0 items-center justify-end">
          {showAction ? (
            <Button
              type="button"
              variant="ghost"
              size="default"
              className="h-8 gap-1 px-2.5 text-xs font-medium"
              aria-label={`Show ${remaining} more`}
              onClick={onShowMore}
            >
              More
              <ChevronRightIcon className="size-4" aria-hidden />
            </Button>
          ) : (
            <span className="pointer-events-none size-8 shrink-0 opacity-0" aria-hidden>
              ·
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
